import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from './card';
import { Skeleton } from './skeleton';

/**
 * MetricCard component for displaying performance metrics with icons and trend indicators
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - The metric title/label
 * @param {string|number} props.value - The metric value to display
 * @param {string} [props.subtitle] - Optional subtitle text
 * @param {React.ReactNode} [props.icon] - Optional icon component
 * @param {'positive'|'negative'|'neutral'} [props.trend] - Trend indicator for color coding
 * @param {boolean} [props.isLoading] - Loading state
 * @param {string} [props.className] - Additional CSS classes
 */
export function MetricCard({
  title,
  value,
  subtitle,
  icon,
  trend = 'neutral',
  isLoading = false,
  className,
  ...props
}) {
  // Color classes based on trend - using design system colors
  const trendColors = {
    positive: 'text-green-600 dark:text-green-400',
    negative: 'text-red-600 dark:text-red-400',
    neutral: 'text-foreground'
  };

  const valueColorClass = trendColors[trend] || trendColors.neutral;

  if (isLoading) {
    return (
      <Card className={cn('p-3 xs:p-4 sm:p-6 min-h-[120px] xs:min-h-[130px] sm:min-h-[140px]', className)} {...props}>
        <div className="space-y-2 xs:space-y-3">
          <div className="flex items-center space-x-2">
            <Skeleton className="h-3 xs:h-4 w-3 xs:w-4 rounded" />
            <Skeleton className="h-3 xs:h-4 w-16 xs:w-20 sm:w-24" />
          </div>
          <Skeleton className="h-6 xs:h-7 sm:h-8 w-12 xs:w-14 sm:w-16" />
          <Skeleton className="h-2 xs:h-3 w-14 xs:w-16 sm:w-20" />
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn(
      'p-3 xs:p-4 sm:p-6 transition-all duration-200 hover:bg-muted/50 hover:shadow-md min-h-[120px] xs:min-h-[130px] sm:min-h-[140px] touch-manipulation',
      className
    )} {...props}>
      <div className="space-y-1.5 xs:space-y-2 h-full flex flex-col">
        {/* Title with optional icon */}
        <div className="flex items-center space-x-1.5 xs:space-x-2 min-h-[20px] xs:min-h-[24px]">
          {icon && (
            <div className="flex-shrink-0" aria-hidden="true">
              {icon}
            </div>
          )}
          <h3 className="text-xs xs:text-sm font-medium text-muted-foreground truncate leading-tight">
            {title}
          </h3>
        </div>

        {/* Main value */}
        <div className={cn(
          'text-lg xs:text-xl sm:text-2xl font-bold tabular-nums leading-tight flex-1 flex items-center',
          valueColorClass
        )}>
          <span className="truncate">{value}</span>
        </div>

        {/* Optional subtitle */}
        {subtitle && (
          <p className="text-xxs xs:text-xs text-muted-foreground leading-tight mt-auto">
            {subtitle}
          </p>
        )}
      </div>
    </Card>
  );
}

export default MetricCard;